import {type MatrixClient} from 'matrix-js-sdk'
import {type ImageInfo} from 'matrix-js-sdk/lib/@types/media'

import {logger} from '#/logger'
import {generateBlurhash} from '#/state/matrix/blurhash'
import {
  clearMediaConfigCache,
  getMediaConfig,
  isFileSizeAcceptable,
} from '#/state/matrix/mediaConfig'
import {
  generateThumbnail,
  shouldIncludeThumbnail,
} from '#/state/matrix/thumbnail'
import {type AttachmentToPick, msgtypeFromMime} from '#/state/matrix/types'
import {uploadToMatrix} from '#/state/matrix/upload'

export type PendingUpload = {
  id: string
  attachment: AttachmentToPick
  status: 'pending' | 'uploading' | 'sending' | 'sent' | 'failed'
  progress: number // 0-1
  error?: string
  abortController: AbortController
}

export type UploadQueueCallbacks = {
  onStatusChange: (
    id: string,
    status: PendingUpload['status'],
    progress?: number,
  ) => void
  onComplete: (id: string) => void
  onError: (id: string, error: string) => void
  sendAttachment: (attachment: {
    msgtype: 'm.image' | 'm.video' | 'm.file' | 'm.audio'
    url: string
    body: string
    info: ImageInfo
    filename?: string
  }) => Promise<void>
}

/**
 * Process a queue of attachments sequentially.
 * Each attachment goes through: validate → thumbnail + blurhash → upload thumbnail → upload file → send event
 */
export async function processUploadQueue(
  client: MatrixClient,
  attachments: AttachmentToPick[],
  callbacks: UploadQueueCallbacks,
): Promise<void> {
  console.log(
    '[UploadQueue] Starting queue with',
    attachments.length,
    'attachments',
  )
  const mediaConfig = await getMediaConfig(client)
  console.log('[UploadQueue] Media config:', mediaConfig)

  for (const attachment of attachments) {
    console.log(
      '[UploadQueue] Processing:',
      attachment.name,
      attachment.mime,
      attachment.size,
      'bytes',
    )
    const id = `upload-${Date.now()}-${Math.random().toString(36).slice(2)}`
    const abortController = new AbortController()

    // Check file size
    if (!isFileSizeAcceptable(attachment.size, mediaConfig.maxUploadSize)) {
      callbacks.onError(
        id,
        `File too large (max ${
          mediaConfig.maxUploadSize
            ? Math.round(mediaConfig.maxUploadSize / 1024 / 1024) + 'MB'
            : 'unknown'
        })`,
      )
      continue
    }

    callbacks.onStatusChange(id, 'uploading', 0)

    try {
      const msgtype = msgtypeFromMime(attachment.mime)
      let thumbnailMxcUrl: string | undefined
      let thumbnailInfo: ImageInfo['thumbnail_info'] | undefined
      let blurhash: string | null = null

      // Generate thumbnail + blurhash for images
      if (
        msgtype === 'm.image' &&
        attachment.width > 0 &&
        attachment.height > 0
      ) {
        try {
          const [thumbResult, blurhashResult] = await Promise.all([
            generateThumbnail(
              attachment.uri,
              attachment.width,
              attachment.height,
              attachment.size,
            ),
            generateBlurhash(
              attachment.uri,
              attachment.width,
              attachment.height,
            ),
          ])
          blurhash = blurhashResult

          if (
            thumbResult &&
            shouldIncludeThumbnail(attachment.size, thumbResult.size || 0)
          ) {
            // Upload thumbnail
            const thumbUpload = await uploadToMatrix(
              client,
              {
                uri: thumbResult.uri,
                name: `thumb_${attachment.name}`,
                type: thumbResult.mimeType,
              },
              undefined,
              abortController,
            )
            thumbnailMxcUrl = thumbUpload.contentUri
            thumbnailInfo = {
              mimetype: thumbResult.mimeType,
              w: thumbResult.width,
              h: thumbResult.height,
              size: thumbResult.size,
            }
          }
        } catch (e) {
          // Thumbnail/blurhash failure shouldn't block the upload
          logger.warn('Failed to generate thumbnail/blurhash', {safeMessage: e})
        }
      }

      callbacks.onStatusChange(id, 'uploading', 0.1)

      // Upload main file
      const uploadResult = await uploadToMatrix(
        client,
        {
          uri: attachment.uri,
          name: attachment.name,
          type: attachment.mime,
        },
        progress => {
          const pct = progress.total
            ? 0.1 + (progress.loaded / progress.total) * 0.8
            : 0.5
          callbacks.onStatusChange(id, 'uploading', pct)
        },
        abortController,
      )

      callbacks.onStatusChange(id, 'sending', 0.95)

      // Build message info
      const info: ImageInfo & {
        'xyz.amorgan.blurhash'?: string
        duration?: number
      } = {
        mimetype: attachment.mime,
        size: attachment.size,
      }

      if (attachment.width > 0) info.w = attachment.width
      if (attachment.height > 0) info.h = attachment.height
      if (thumbnailMxcUrl) info.thumbnail_url = thumbnailMxcUrl
      if (thumbnailInfo) info.thumbnail_info = thumbnailInfo
      if (blurhash) info['xyz.amorgan.blurhash'] = blurhash

      // Send the message event
      await callbacks.sendAttachment({
        msgtype: msgtype as 'm.image' | 'm.video' | 'm.file' | 'm.audio',
        url: uploadResult.contentUri,
        body: attachment.name,
        info,
        filename: attachment.name,
      })

      callbacks.onStatusChange(id, 'sent', 1)
      callbacks.onComplete(id)
    } catch (error: any) {
      // Clear media config cache on 413 so limits are re-fetched
      if (error?.httpStatus === 413) {
        clearMediaConfigCache()
      }

      const message = error?.message || 'Upload failed'
      console.error('[UploadQueue] Item failed:', error)
      callbacks.onError(id, message)
      logger.error('Upload queue item failed', {safeMessage: error})
      // Continue with remaining files
    }
  }
}
